import React from "react";
import { useTranslate } from "@refinedev/core";
import { Col, Empty, Flex, Row } from "antd";
import type {
  AddonActivation,
  AddonDetail,
  AddonItemStat,
  QuizPreparation,
} from "types/";
import { AnalyticsCard } from "../AnalyticsCard";
import { AnalyticsLineChart } from "../AnalyticsLineChart";
import { analyticsTheme } from "../analyticsTheme";
import { ItemRows, type ItemRow } from "../ItemRows";
import { AddonItemOpenRateCard } from "./AddonItemOpenRateCard";
import { AddonPanelHeader } from "./AddonPanelHeader";
import { chartPlotHeight } from "./utils/chartSizing";
import { ADDON_COLORS, mergeItems, quizEffect, reachCurve } from "./utils/metrics";
import { QuizEffectCard } from "./QuizEffectCard";
import { useCoverageAndTimeStats } from "./hooks/useCoverageAndTimeStats";

const T_PREFIX = "media.projects.analytics.addons";
const KNOWN_COLOR = analyticsTheme.datasetBlue;
const REVIEW_COLOR = analyticsTheme.datasetGrey;

export type FlashcardsPanelProps = {
  activation: AddonActivation;
  detail: AddonDetail;
  /** null without a quiz, or when nobody finished it: no quiz effect card. */
  preparation: QuizPreparation | null;
  /** The preparation request failed: the quiz effect card says so. */
  preparationFailed?: boolean;
  projectSessions: number;
};

export const FlashcardsPanel: React.FC<FlashcardsPanelProps> = ({
  activation,
  detail,
  preparation,
  preparationFailed = false,
  projectSessions,
}) => {
  const t = useTranslate();
  const stats = useCoverageAndTimeStats(activation);
  const items = detail.items;
  const effect = preparation ? quizEffect(preparation, "flashcards") : null;
  // One entry per position: an item edited since is listed once per wording.
  const positions = mergeItems(items);

  const flipsNote = (item: AddonItemStat) =>
    item.flips == null
      ? null
      : t(`${T_PREFIX}.flashcards.flips`, { count: item.flips });

  // Quick recall: the viewer's own rating after flipping a card.
  const rated = positions.filter((i) => (i.known ?? 0) + (i.review ?? 0) > 0);
  const totalFlips = items.reduce((sum, i) => sum + (i.flips ?? 0), 0);
  const ratingRows: ItemRow[] = rated.map((item) => {
    const known = item.known ?? 0;
    const review = item.review ?? 0;
    const share = (known / (known + review)) * 100;
    const n = t(`${T_PREFIX}.itemNumber.flashcards`, { n: item.item_index + 1 });
    return {
      key: String(item.item_index),
      label: n,
      text: item.label ?? t(`${T_PREFIX}.itemFallback`, { n }),
      value: t(`${T_PREFIX}.flashcards.knownShare`, { pct: Math.round(share) }),
      tooltip: t(`${T_PREFIX}.flashcards.ratingTooltip`, { known, review }),
      bar: { percent: share, color: KNOWN_COLOR, trackColor: REVIEW_COLOR },
    };
  });

  return (
    <Flex vertical gap={16}>
      <AddonPanelHeader
        addon="flashcards"
        activation={activation}
        projectSessions={projectSessions}
        // The positions also hold items removed since they were seen.
        count={t(`${T_PREFIX}.itemCount`, {
          count: activation.total_items ?? positions.length,
        })}
        stats={stats}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <AddonItemOpenRateCard
            addon="flashcards"
            activation={activation}
            positions={positions}
            itemNote={flipsNote}
          />
        </Col>

        <Col xs={24} lg={12}>
          <AnalyticsLineChart
            title={t(`${T_PREFIX}.flashcards.deckTitle`)}
            subtitle={t(`${T_PREFIX}.flashcards.deckSubtitle`)}
            labels={positions.map((i) =>
              t(`${T_PREFIX}.itemNumber.flashcards`, { n: i.item_index + 1 })
            )}
            datasets={[
              {
                label: t(`${T_PREFIX}.flashcards.deckSeries`),
                data: reachCurve(positions),
                color: ADDON_COLORS.flashcards,
              },
            ]}
            plotHeight={chartPlotHeight(200)}
            chartOptions={{ plugins: { legend: { display: false } } }}
          />
        </Col>
        <Col xs={24} lg={12}>
          <AnalyticsCard
            title={t(`${T_PREFIX}.flashcards.ratingsTitle`)}
            subtitle={t(`${T_PREFIX}.flashcards.ratingsSubtitle`)}
          >
            {rated.length === 0 ? (
              <Empty
                // Cards were flipped but no rating was recorded.
                description={t(
                  totalFlips > 0
                    ? `${T_PREFIX}.empty.noRatings`
                    : `${T_PREFIX}.empty.noFlips`
                )}
              />
            ) : (
              <ItemRows rows={ratingRows} />
            )}
          </AnalyticsCard>
        </Col>

        {effect || preparationFailed ? (
          <Col xs={24} lg={12}>
            <QuizEffectCard effect={effect} />
          </Col>
        ) : null}
      </Row>
    </Flex>
  );
};
